import { useFpCampaignCartCalculate } from '@cart-services/hooks/useFpCampaignCartCalculate'
import { useFpCampaignCartService } from '@cart-services/hooks/useFpCampaignCartService'
import { useFpCampaignCoupons } from '@cart-services/hooks/useFpCampaignCoupons'
import { useFpCampaignProductsInfo } from '@cart-services/hooks/useFpCampaignProductsInfo'
import { ChipFreeShipping } from '@cart-ui/ChipFreeShipping/ChipFreeShipping'
import { sendFastpassBeginCheckoutEvent } from '@dni-analysis'
import { generateItemVariant } from '@dni-analysis/events/utils'
import { useDialog } from '@dni-hooks/useDialog'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { Footer, type FooterProps } from '@pets-ui/Footer/Footer'
import { HStack } from '@pets-ui/HStack/HStack'
import { CartOutlined } from '@pets-ui/icons'
import { useRef } from 'react'
import { ButtonCampaignFooterAmount } from '../../../../../components/ButtonCampaignFooterAmount/ButtonCampaignFooterAmount'
import { useContextCampaignCart } from '../CartProvider/CampaignCartProvider'
import { CART_FORM_ID } from '../CartProvider/CartFormProvider'
import { DIALOG_ORDER_DETAIL, DialogOrderDetail, type DialogOrderDetailProps } from '../DialogOrderDetail/DialogOrderDetail'

export interface FooterCheckoutProps extends FooterProps {
  currency?: string
  amount?: number
  isCartEmpty?: boolean
  discountTotalWithoutShippingCoupon?: number
}

export const FooterCheckout = (props: FooterCheckoutProps) => {
  const { currency, amount, isCartEmpty, discountTotalWithoutShippingCoupon, ...rest } = props
  const { campaignId, countryCode, campaignCode, isSubmitLoading } = useContextCampaignCart()
  const { items: cartItems } = useFpCampaignCartService(campaignId)
  const { productMap } = useFpCampaignProductsInfo({ campaignId, campaignCode, countryCode })
  const { openDialog: openDialogPaymentDetail, closeDialog } = useDialog<DialogOrderDetailProps>(DIALOG_ORDER_DETAIL)
  const { calculateResult: campaignCouponCalculateResult, isLoadingAvailableCoupons, isLoadingProducts } = useFpCampaignCoupons(campaignId, countryCode, campaignCode)
  const { isStale } = useFpCampaignCartCalculate(campaignId, campaignCode)
  const checkoutButtonRef = useRef<HTMLButtonElement>(null)
  const isLoading = isLoadingAvailableCoupons || isLoadingProducts || isSubmitLoading || isStale

  const handleDialogOrderDetailCheckout = () => {
    if (checkoutButtonRef.current) {
      openDialogPaymentDetail({
        handleCheckout: () => {
          checkoutButtonRef.current?.click()
          closeDialog()
        },
      })
    }
  }

  const handleCheckout = useCallbackEvent(() => {
    if (campaignCode) {
      sendFastpassBeginCheckoutEvent({
        campaign_code: campaignCode,
        currency,
        value: campaignCouponCalculateResult?.total,
        items: cartItems.map((item) => {
          const product = productMap?.[item.cartItemId]

          return {
            currency,
            item_id: String(item.cartItemId),
            quantity: item.quantity,
            item_brand: item.project_code,
            item_name: product?.name,
            price: product?.sale_price ?? 0,
            item_variant: generateItemVariant({
              productName: product?.name,
              attributes: item.attributes,
            }),
          }
        }),
      })
    }
  })

  return (
    <>
      <DialogOrderDetail />
      <Footer {...rest}>
        <ButtonCampaignFooterAmount
          currency={currency}
          amount={amount}
          onClick={handleDialogOrderDetailCheckout}
          shippingFee={campaignCouponCalculateResult?.shipping_total || 0}
          discountTotalWithoutShippingCoupon={discountTotalWithoutShippingCoupon}
        />
        <Box flexGrow={1} />
        <HStack gap={2}>
          <Button
            rootRef={checkoutButtonRef}
            form={CART_FORM_ID}
            startIcon={<CartOutlined />}
            type="submit"
            loading={isLoading}
            disabled={isLoading || isCartEmpty}
            onClick={handleCheckout}
          >
            前往結帳
            <ChipFreeShipping isCartEmpty={isCartEmpty} shippingFee={campaignCouponCalculateResult?.shipping_total || 0} />
          </Button>
        </HStack>
      </Footer>
    </>
  )
}
